import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import 'swiper/swiper-bundle.css';
import 'flatpickr/dist/flatpickr.css';
import App from './App';
import { AppWrapper } from './components/common/PageMeta';
import { ThemeProvider } from './context/ThemeContext';
import AppMuiThemeProvider from './theme/AppMuiThemeProvider';
import { store } from './store';
import { logViteEnv } from './utils/logViteEnv';

logViteEnv();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

// PWA safety:
// In dev we intentionally do NOT use the service worker. Also, if an older SW
// is still installed in the browser cache, unregister it to avoid Workbox spam.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  if (import.meta.env.MODE !== 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((r) => r.unregister())),
        )
        .catch(() => {
          // Ignore unregister errors in dev; SW might already be gone.
        });
    });
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  // Fail loudly in dev; in prod show a minimal message rather than a blank page.
  const message =
    'Failed to mount app: #root element is missing from index.html';
  if (import.meta.env.DEV) {
    throw new Error(message);
  } else {
    // eslint-disable-next-line no-console
    console.error(message);
    const fallback = document.createElement('div');
    fallback.textContent = 'Application failed to load. Please refresh.';
    fallback.style.padding = '16px';
    document.body?.appendChild(fallback);
  }
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AppMuiThemeProvider>
              <AppWrapper>
                <App />
              </AppWrapper>
            </AppMuiThemeProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Provider>
    </StrictMode>,
  );
}
